import React from 'react';
import { apiFetch } from './api';
import { useNavigate } from 'react-router-dom';
import { useDashboardStats } from './DashboardStatsContext';

function Dashboard() {
  const navigate = useNavigate();
  const { stats, loading, refreshStats } = useDashboardStats();
  
  // Stati per le statistiche
  const [dailyStats, setDailyStats] = React.useState({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
  const [loadingDaily, setLoadingDaily] = React.useState(true);
  const [monthlyStats, setMonthlyStats] = React.useState({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
  const [loadingMonthly, setLoadingMonthly] = React.useState(true);
  
  // Stati per i provider
  const [providers, setProviders] = React.useState([]);
  const [loadingProviders, setLoadingProviders] = React.useState(true);

  // Stati hopper
  const [hoppers, setHoppers] = React.useState([]);
  const [loadingHoppers, setLoadingHoppers] = React.useState(true);
  const [hopperExportDates, setHopperExportDates] = React.useState({});
  
  // Stati per il download
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const firstOfMonth = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
  const today = now.toISOString().slice(0, 10);
  const [exportFrom, setExportFrom] = React.useState(firstOfMonth);
  const [exportTo, setExportTo] = React.useState(today);
  const [exportServizio, setExportServizio] = React.useState('');

  const serviziDisponibili = [
    'Comunicati', 'Iniziative', 'Pagamenti', 'Personalizzato',
    'Rassegna', 'Scadenzario', 'Tributi'
  ];
  
  const lastLogin = new Date().toLocaleString('it-IT');

  // Carica statistiche giornaliere
  React.useEffect(() => {
    async function fetchDailyStats() {
      setLoadingDaily(true);
      try {
        const response = await apiFetch('/api/Mail/conteggio_giornaliero');
        if (response.ok) {
          const data = await response.json();
          setDailyStats({
            inviTotali: data.inviTotali,
            inviRiusciti: data.inviRiusciti,
            inviFalliti: data.inviFalliti
          });
        } else {
          setDailyStats({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
        }
      } catch {
        setDailyStats({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
      }
      setLoadingDaily(false);
    }
    
    fetchDailyStats();
  }, []);

  // Carica statistiche mensili
  React.useEffect(() => {
    async function fetchMonthlyStats() {
      setLoadingMonthly(true);
      try {
        const response = await apiFetch(`/api/Mail/conteggio_mensile?mese=${currentMonth}`);
        if (response.ok) {
          const data = await response.json();
          setMonthlyStats({
            inviTotali: data.inviTotali,
            inviRiusciti: data.inviRiusciti,
            inviFalliti: data.inviFalliti
          });
        } else {
          setMonthlyStats({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
        }
      } catch {
        setMonthlyStats({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
      }
      setLoadingMonthly(false);
    }
    
    fetchMonthlyStats();
  }, [currentMonth]);

  // Carica provider
  React.useEffect(() => {
    async function fetchProviders() {
      setLoadingProviders(true);
      try {
        const response = await apiFetch('/api/Provider/TestAll');
        if (response.ok) {
          const data = await response.json();
          setProviders(data);
        } else {
          setProviders([]);
        }
      } catch {
        setProviders([]);
      }
      setLoadingProviders(false);
    }

    fetchProviders();
  }, []);

  // Carica hopper + polling ogni 5 secondi
  const fetchHoppers = React.useCallback(async () => {
    try {
      const response = await apiFetch('/api/Mail/hoppers');
      if (response.ok) {
        const data = await response.json();
        setHoppers(data);
        // Inizializza date export per ogni hopper (solo la prima volta)
        setHopperExportDates(prev => {
          const updated = { ...prev };
          data.forEach(h => {
            if (!updated[h.providerId]) {
              updated[h.providerId] = { from: firstOfMonth, to: today };
            }
          });
          return updated;
        });
      } else {
        setHoppers([]);
      }
    } catch {
      setHoppers([]);
    }
    setLoadingHoppers(false);
  }, [firstOfMonth, today]);

  React.useEffect(() => {
    fetchHoppers();
    const interval = setInterval(fetchHoppers, 5000);
    return () => clearInterval(interval);
  }, [fetchHoppers]);

  // Aggiorna statistiche globali
  React.useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible') {
        refreshStats();
      }
    }
    
    refreshStats();
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshStats]);

  // Gestione download statistiche da DB
  const handleDownloadStats = async () => {
    try {
      const params = new URLSearchParams();
      if (exportFrom)     params.set('from', exportFrom);
      if (exportTo)       params.set('to', exportTo);
      if (exportServizio) params.set('servizio', exportServizio);

      const response = await apiFetch(`/api/Mail/jobs/export?${params}`, {
        method: 'GET',
        headers: { 'Accept': 'text/csv, application/octet-stream, */*' }
      });

      if (!response.ok)
        throw new Error(`Errore del server: ${response.status} ${response.statusText}`);

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('text/html'))
        throw new Error('Il server ha restituito HTML invece del file CSV');

      const disposition = response.headers.get('Content-Disposition');
      let filename = `email_export_${exportFrom}_${exportTo}.csv`;
      if (disposition) {
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          const standardMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
          if (standardMatch) filename = standardMatch[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Il file scaricato è vuoto');

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      alert(`Errore durante il download: ${err.message}`);
    }
  };

  // Download report per singolo hopper
  const handleDownloadHopper = async (providerId, displayName) => {
    try {
      const dates = hopperExportDates[providerId] || { from: firstOfMonth, to: today };
      const params = new URLSearchParams();
      if (dates.from) params.set('from', dates.from);
      if (dates.to)   params.set('to',   dates.to);

      const response = await apiFetch(`/api/Mail/hoppers/${providerId}/export?${params}`, {
        method: 'GET',
        headers: { 'Accept': 'text/csv, application/octet-stream, */*' }
      });

      if (!response.ok) throw new Error(`Errore del server: ${response.status}`);

      const disposition = response.headers.get('Content-Disposition');
      let filename = `hopper_${displayName}_${dates.from}_${dates.to}.csv`;
      if (disposition) {
        const m = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (m) filename = m[1].replace(/['"]/g, '');
      }

      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Il file scaricato è vuoto');

      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Errore download hopper: ${err.message}`);
    }
  };

  // Aggiorna statistiche
  const handleRefresh = async () => {
    // Refresh delle statistiche globali
    refreshStats();
    
    // Refresh delle statistiche giornaliere
    setLoadingDaily(true);
    try {
      const response = await apiFetch('/api/Mail/conteggio_giornaliero');
      if (response.ok) {
        const data = await response.json();
        setDailyStats({
          inviTotali: data.inviTotali,
          inviRiusciti: data.inviRiusciti,
          inviFalliti: data.inviFalliti
        });
      } else {
        setDailyStats({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
      }
    } catch {
      setDailyStats({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
    }
    setLoadingDaily(false);
    
    // Refresh delle statistiche mensili
    setLoadingMonthly(true);
    try {
      const response = await apiFetch(`/api/Mail/conteggio_mensile?mese=${currentMonth}`);
      if (response.ok) {
        const data = await response.json();
        setMonthlyStats({
          inviTotali: data.inviTotali,
          inviRiusciti: data.inviRiusciti,
          inviFalliti: data.inviFalliti
        });
      } else {
        setMonthlyStats({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
      }
    } catch {
      setMonthlyStats({ inviTotali: '-', inviRiusciti: '-', inviFalliti: '-' });
    }
    setLoadingMonthly(false);
    
    // Refresh dei provider
    setLoadingProviders(true);
    try {
      const response = await apiFetch('/api/Provider/TestAll');
      if (response.ok) {
        const data = await response.json();
        setProviders(data);
      } else {
        setProviders([]);
      }
    } catch {
      setProviders([]);
    }
    setLoadingProviders(false);

    // Refresh hopper
    fetchHoppers();
  };

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="text-primary">
                <i className="fas fa-tachometer-alt"></i> Dashboard
              </h1>
              <p className="text-muted">Benvenuto nel sistema di invio email ANUTEL ETS</p>
            </div>
            <div className="text-end">
              <small className="text-muted d-block">
                Ultimo accesso: {lastLogin}
              </small>
              <button
                className="btn btn-outline-danger btn-sm mt-2"
                onClick={() => {
                  localStorage.removeItem('token');
                  navigate('/');
                }}
              >
                <i className="fas fa-sign-out-alt me-1"></i> Esci
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Debug Info */}
      <div className="alert alert-info">
        <small>
          <strong>DEBUG:</strong> Provider caricati: {providers.length} | Provider attivi: {stats?.activeProviders ?? '-'}
        </small>
      </div>

      {/* Statistiche */}
      <div className="row mb-4">
        <div className="col-12 d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h3><i className="fas fa-chart-bar"></i> Statistiche</h3>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <button 
              className="btn btn-outline-primary btn-sm" 
              onClick={handleRefresh} 
              disabled={loading || loadingDaily}
              type="button"
            >
              {(loading || loadingDaily) ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : (
                <i className="fas fa-sync-alt"></i>
              )} Aggiorna
            </button>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{width:'auto'}}
              value={exportFrom}
              onChange={e => setExportFrom(e.target.value)}
              title="Data inizio"
            />
            <span className="text-muted">→</span>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{width:'auto'}}
              value={exportTo}
              onChange={e => setExportTo(e.target.value)}
              title="Data fine"
            />
            <select
              className="form-select form-select-sm"
              style={{width:'auto', minWidth:130}}
              value={exportServizio}
              onChange={e => setExportServizio(e.target.value)}
            >
              <option value="">Tutti i servizi</option>
              {serviziDisponibili.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button
              className="btn btn-outline-success btn-sm"
              onClick={handleDownloadStats}
              type="button"
            >
              <i className="fas fa-download"></i> Scarica Statistiche
            </button>
          </div>
        </div>

        {/* Statistiche di oggi */}
        <div className="col-12 mb-2">
          <div className="row g-2">
            <div className="col-md-4">
              <div className="card border-primary card-hover h-100">
                <div className="card-body text-center">
                  <i className="fas fa-envelope fa-2x text-primary mb-2"></i>
                  <h5 className="card-title">
                    {loadingDaily ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      dailyStats.inviTotali
                    )}
                  </h5>
                  <p className="card-text text-muted">Email Oggi</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-success card-hover h-100">
                <div className="card-body text-center">
                  <i className="fas fa-check-circle fa-2x text-success mb-2"></i>
                  <h5 className="card-title">
                    {loadingDaily ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      dailyStats.inviRiusciti
                    )}
                  </h5>
                  <p className="card-text text-muted">Inviate con Successo</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-danger card-hover h-100">
                <div className="card-body text-center">
                  <i className="fas fa-exclamation-circle fa-2x text-danger mb-2"></i>
                  <h5 className="card-title">
                    {loadingDaily ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      dailyStats.inviFalliti
                    )}
                  </h5>
                  <p className="card-text text-muted">Fallite</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiche mensili */}
        <div className="col-12 mt-2">
          <div className="row g-2">
            <div className="col-md-4">
              <div className="card border-primary card-hover h-100">
                <div className="card-body text-center">
                  <i className="fas fa-envelope fa-2x text-primary mb-2"></i>
                  <h5 className="card-title">
                    {loadingMonthly ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      monthlyStats.inviTotali
                    )}
                  </h5>
                  <p className="card-text text-muted">Email Mensili</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-success card-hover h-100">
                <div className="card-body text-center">
                  <i className="fas fa-check-circle fa-2x text-success mb-2"></i>
                  <h5 className="card-title">
                    {loadingMonthly ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      monthlyStats.inviRiusciti
                    )}
                  </h5>
                  <p className="card-text text-muted">Inviate con Successo</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-danger card-hover h-100">
                <div className="card-body text-center">
                  <i className="fas fa-exclamation-circle fa-2x text-danger mb-2"></i>
                  <h5 className="card-title">
                    {loadingMonthly ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      monthlyStats.inviFalliti
                    )}
                  </h5>
                  <p className="card-text text-muted">Fallite</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Servizi di Invio */}
      <div className="row">
        <div className="col-12 d-flex justify-content-between align-items-center mb-3">
          <h3><i className="fas fa-paper-plane"></i> Servizi di Invio</h3>
        </div>
      </div>
      
      <div className="row">
        {/* Personalizzato */}
        <div className="col-md-6 col-lg-3 mb-4">
          <div className="card h-100 card-hover border-secondary">
            <div className="card-body text-center">
              <i className="fas fa-edit fa-3x text-secondary mb-3"></i>
              <h5 className="card-title">Personalizzato</h5>
              <p className="card-text">Invia email a destinatari personalizzati.</p>
              <button className="btn btn-secondary" onClick={() => navigate('/personalizzato')}>
                <i className="fas fa-arrow-right"></i> Accedi
              </button>
            </div>
          </div>
        </div>

        {/* Pagamenti */}
        <div className="col-md-6 col-lg-3 mb-4">
          <div className="card h-100 card-hover border-warning">
            <div className="card-body text-center">
              <i className="fas fa-coins fa-3x text-warning mb-3"></i>
              <h5 className="card-title">Pagamenti</h5>
              <p className="card-text">Comunicazioni per pagamenti e tessere associative.</p>
              <button className="btn btn-warning" onClick={() => navigate('/pagamenti')}>
                <i className="fas fa-arrow-right"></i> Accedi
              </button>
            </div>
          </div>
        </div>

        {/* Iniziative */}
        <div className="col-md-6 col-lg-3 mb-4">
          <div className="card h-100 card-hover border-success">
            <div className="card-body text-center">
              <i className="fas fa-calendar-alt fa-3x text-success mb-3"></i>
              <h5 className="card-title">Iniziative</h5>
              <p className="card-text">Gestisci le comunicazioni per eventi formativi e webinar.</p>
              <button className="btn btn-success" onClick={() => navigate('/iniziative')}>
                <i className="fas fa-arrow-right"></i> Accedi
              </button>
            </div>
          </div>
        </div>

        {/* Comunicati */}
        <div className="col-md-6 col-lg-3 mb-4">
          <div className="card h-100 card-hover border-info">
            <div className="card-body text-center">
              <i className="fas fa-bullhorn fa-3x text-info mb-3"></i>
              <h5 className="card-title">Comunicati</h5>
              <p className="card-text">Invia comunicati ufficiali agli enti e ai referenti ANUTEL ETS.</p>
              <button className="btn btn-info" onClick={() => navigate('/comunicati')}>
                <i className="fas fa-arrow-right"></i> Accedi
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Scadenzario */}
        <div className="col-md-6 col-lg-3 mb-4">
          <div className="card h-100 card-hover border-danger">
            <div className="card-body text-center">
              <i className="fas fa-calendar-day fa-3x text-danger mb-3"></i>
              <h5 className="card-title">Scadenzario</h5>
              <p className="card-text">Invia promemoria e avvisi di scadenza agli enti e ai referenti ANUTEL ETS.</p>
              <button className="btn btn-danger" onClick={() => navigate('/scadenzario')}>
                <i className="fas fa-arrow-right"></i> Accedi
              </button>
            </div>
          </div>
        </div>

        {/* Rassegna */}
        <div className="col-md-6 col-lg-3 mb-4">
          <div className="card h-100 card-hover border-primary">
            <div className="card-body text-center">
              <i className="fas fa-newspaper fa-3x text-primary mb-3"></i>
              <h5 className="card-title">Rassegna Stampa</h5>
              <p className="card-text">Invia la rassegna stampa quotidiana agli enti e ai referenti ANUTEL ETS.</p>
              <button className="btn btn-primary" onClick={() => navigate('/rassegna')}>
                <i className="fas fa-arrow-right"></i> Accedi
              </button>
            </div>
          </div>
        </div>

        {/* Tributi & Bilancio */}
        <div className="col-md-6 col-lg-3 mb-4">
          <div className="card h-100 card-hover border-primary">
            <div className="card-body text-center">
              <i className="fas fa-balance-scale fa-3x text-primary mb-3"></i>
              <h5 className="card-title">Tributi & Bilancio</h5>
              <p className="card-text">Invia la rivista tributi e bilancio agli enti e ai referenti ANUTEL ETS.</p>
              <button className="btn btn-primary" onClick={() => navigate('/tributi')}>
                <i className="fas fa-arrow-right"></i> Accedi
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Code di invio — Hopper */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3><i className="fas fa-inbox"></i> Code di Invio</h3>
            <div className="d-flex align-items-center gap-2">
              {loadingHoppers && (
                <span className="spinner-border spinner-border-sm text-primary"></span>
              )}
              <small className="text-muted">aggiornamento automatico ogni 5s</small>
            </div>
          </div>

          {hoppers.length === 0 && !loadingHoppers ? (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Nessun provider abilitato trovato.
            </div>
          ) : (
            <div className="row g-3">
              {hoppers.map(h => {
                const dates = hopperExportDates[h.providerId] || { from: firstOfMonth, to: today };
                const hasActivity = h.pendingCount > 0 || h.sendingCount > 0;
                const borderClass = h.sendingCount > 0
                  ? 'border-warning'
                  : h.pendingCount > 0
                    ? 'border-info'
                    : 'border-secondary';

                return (
                  <div className="col-md-6 col-lg-4 col-xl-3" key={h.providerId}>
                    <div className={`card h-100 ${borderClass}`} style={{ borderWidth: hasActivity ? 2 : 1 }}>
                      <div className="card-header d-flex justify-content-between align-items-start py-2">
                        <div>
                          <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>{h.displayName}</span>
                          <br />
                          <span className={`badge ${h.providerTypeName === 'PEC' ? 'bg-danger' : 'bg-primary'} me-1`} style={{ fontSize: '0.7rem' }}>
                            {h.providerTypeName}
                          </span>
                          <span className={`badge ${h.isSmtp ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>
                            {h.isSmtp ? 'SMTP' : 'Pickup'}
                          </span>
                        </div>
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>#{h.providerId}</span>
                      </div>

                      <div className="card-body py-2 px-3">
                        <div className="row g-1 text-center mb-2">
                          <div className="col-6">
                            <div className={`rounded p-1 ${h.pendingCount > 0 ? 'bg-info bg-opacity-10' : ''}`}>
                              <div className="fw-bold text-info">{h.pendingCount}</div>
                              <div style={{ fontSize: '0.7rem' }} className="text-muted">In coda</div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className={`rounded p-1 ${h.sendingCount > 0 ? 'bg-warning bg-opacity-10' : ''}`}>
                              <div className="fw-bold text-warning">{h.sendingCount}</div>
                              <div style={{ fontSize: '0.7rem' }} className="text-muted">In invio</div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="rounded p-1">
                              <div className="fw-bold text-success">{h.sentTodayCount}</div>
                              <div style={{ fontSize: '0.7rem' }} className="text-muted">Inviati oggi</div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className={`rounded p-1 ${h.failedTodayCount > 0 ? 'bg-danger bg-opacity-10' : ''}`}>
                              <div className={`fw-bold ${h.failedTodayCount > 0 ? 'text-danger' : 'text-muted'}`}>{h.failedTodayCount}</div>
                              <div style={{ fontSize: '0.7rem' }} className="text-muted">Falliti oggi</div>
                            </div>
                          </div>
                        </div>

                        {/* Filtro date per report */}
                        <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            style={{ width: 120, fontSize: '0.72rem' }}
                            value={dates.from}
                            onChange={e => setHopperExportDates(prev => ({
                              ...prev,
                              [h.providerId]: { ...dates, from: e.target.value }
                            }))}
                          />
                          <span className="text-muted" style={{ fontSize: '0.72rem' }}>→</span>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            style={{ width: 120, fontSize: '0.72rem' }}
                            value={dates.to}
                            onChange={e => setHopperExportDates(prev => ({
                              ...prev,
                              [h.providerId]: { ...dates, to: e.target.value }
                            }))}
                          />
                        </div>
                      </div>

                      <div className="card-footer py-2 px-3">
                        <button
                          className="btn btn-outline-success btn-sm w-100"
                          style={{ fontSize: '0.75rem' }}
                          onClick={() => handleDownloadHopper(h.providerId, h.displayName)}
                        >
                          <i className="fas fa-download me-1"></i> Scarica Report
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Test connessioni provider */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3><i className="fas fa-network-wired"></i> Test Connessioni Provider</h3>
            <span className="text-muted">({providers.length} provider)</span>
          </div>
          <div className="card">
            <div className="card-body">
              {loadingProviders ? (
                <div className="d-flex align-items-center">
                  <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                  <span>Test dei provider in corso...</span>
                </div>
              ) : providers.length > 0 ? (
                <div className="row">
                  {providers.map(provider => (
                    <div className="col-md-6 col-lg-4 mb-2" key={provider.id}>
                      <div className={`alert d-flex align-items-center mb-2 ${provider.aviable ? 'alert-success' : 'alert-danger'}`}>
                        <i className={`fas ${provider.aviable ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'} me-2`}></i>
                        <div>
                          <strong>{provider.displayName}</strong><br />
                          <span>{provider.message}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  Nessun provider SMTP configurato o caricato.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;